import React, { useState, useEffect, useCallback } from 'react';
import { usageAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import Pagination from '../../components/common/Pagination';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import toast from 'react-hot-toast';

// ─── Usage bar ─────────────────────────────────────────
const UsageBar = ({ used, limit, color }) => {
  if (limit === -1) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-gray-300 rounded-full" style={{ width: '20%' }} />
        </div>
        <span className="text-xs text-gray-400">{used} / ∞</span>
      </div>
    );
  }

  const pct = Math.min(100, Math.round((used / limit) * 100));
  const isOver = used > limit;

  const barColor =
    isOver ? 'bg-red-500' :
    pct >= 80 ? 'bg-yellow-400' :
    `bg-${color}-500`;

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <span className={`text-xs font-medium ${isOver ? 'text-red-600' : 'text-gray-500'}`}>
        {used} / {limit}
      </span>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────
const UsageManagement = () => {

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);

    try {
      const res = await usageAPI.getAll({ page, limit: 15 });

      setRecords(res.data.data);
      setPagination(res.data.pagination);

    } catch {
      toast.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }

  }, [page]);

  useEffect(() => {
    fetch();
  }, [fetch]);


  const handleReset = async (usageId, type) => {

    if (!window.confirm(`Reset ${type} usage?`)) return;

    try {
      await usageAPI.resetUsage(usageId, type);

      toast.success('Usage reset');

      fetch();

      setDetailRow(null);

    } catch {
      toast.error('Reset failed');
    }

  };


  return (
    <div className="space-y-4">

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">

        {[
          {
            label: 'Customers Over Limit',
            value: records.filter(r => r.customersLimit !== -1 && r.customersUsed > r.customersLimit).length,
            icon: '👥',
            color: 'bg-indigo-50'
          },
          {
            label: 'Receipt Scan Over Limit',
            value: records.filter(r => r.receiptScanLimit !== -1 && r.receiptScanUsed > r.receiptScanLimit).length,
            icon: '🧾',
            color: 'bg-blue-50'
          },
          {
            label: 'Manual Invoice Over Limit',
            value: records.filter(r => r.manualInvoiceLimit !== -1 && r.manualInvoiceUsed > r.manualInvoiceLimit).length,
            icon: '📄',
            color: 'bg-green-50'
          },
          {
            label: 'Items Over Limit',
            value: records.filter(r => r.itemsLimit !== -1 && r.itemsUsed > r.itemsLimit).length,
            icon: '📦',
            color: 'bg-orange-50'
          }
        ].map(c => (

          <div
            key={c.label}
            className={`${c.color} rounded-xl p-4 flex items-center gap-3 border`}
          >
            <span className="text-2xl">{c.icon}</span>

            <div>
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className="text-xl font-bold text-gray-900">{c.value}</p>
            </div>

          </div>

        ))}

      </div>


      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">

        {loading ? (

          <div className="p-12">
            <Spinner />
          </div>

        ) : (

          <>
            <table className="w-full text-sm">

              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">

                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3 w-48">👥 Customers</th>
                  <th className="px-5 py-3 w-48">🧾 Receipt Scans</th>
                  <th className="px-5 py-3 w-48">📄 Manual Invoices</th>
                  <th className="px-5 py-3 w-48">📦 Items</th>
                  <th className="px-5 py-3">Period</th>
                  <th className="px-5 py-3">Actions</th>

                </tr>
              </thead>

              <tbody className="divide-y">

                {records.map(r => {

                  const custOver =
                    r.customersLimit !== -1 &&
                    r.customersUsed > r.customersLimit;

                  const scanOver =
                    r.receiptScanLimit !== -1 &&
                    r.receiptScanUsed > r.receiptScanLimit;

                  const manualOver =
                    r.manualInvoiceLimit !== -1 &&
                    r.manualInvoiceUsed > r.manualInvoiceLimit;

                  const itemsOver =
                    r.itemsLimit !== -1 &&
                    r.itemsUsed > r.itemsLimit;

                  return (

                    <tr
                      key={r._id}
                      className={`hover:bg-gray-50 ${(custOver || scanOver || manualOver || itemsOver) ? 'bg-red-50/30' : ''}`}
                    >

                      <td className="px-5 py-3">

                        <p className="font-medium">
                          {r.customer?.name}
                        </p>

                        <p className="text-xs text-gray-400">
                          {r.customer?.email}
                        </p>

                      </td>

                      <td className="px-5 py-3 text-gray-600">
                        {r.plan?.name || '—'}
                      </td>


                      <td className="px-5 py-3">
                        <UsageBar
                          used={r.customersUsed}
                          limit={r.customersLimit}
                          color="indigo"
                        />
                      </td>

                      <td className="px-5 py-3">
                        <UsageBar
                          used={r.receiptScanUsed}
                          limit={r.receiptScanLimit}
                          color="blue"
                        />
                      </td>

                      <td className="px-5 py-3">
                        <UsageBar
                          used={r.manualInvoiceUsed}
                          limit={r.manualInvoiceLimit}
                          color="green"
                        />
                      </td>

                      <td className="px-5 py-3">
                        <UsageBar
                          used={r.itemsUsed}
                          limit={r.itemsLimit}
                          color="orange"
                        />
                      </td>


                      <td className="px-5 py-3 text-xs text-gray-500">

                        {formatDate(r.periodStart)} → <br />
                        {formatDate(r.periodEnd)}

                      </td>

                      <td className="px-5 py-3">

                        <button
                          onClick={() => setDetailRow(r)}
                          className="text-indigo-600 hover:underline text-xs"
                        >
                          Details
                        </button>

                      </td>

                    </tr>

                  );

                })}


                {records.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center text-gray-400">
                      No usage records yet.
                    </td>
                  </tr>
                )}

              </tbody>
            </table>

            <Pagination
              pagination={pagination}
              onPageChange={setPage}
            />

          </>
        )}

      </div>


      {/* Detail Modal */}
      {detailRow && (

        <Modal
          isOpen={true}
          onClose={() => setDetailRow(null)}
          title="Usage Detail"
          size="md"
        >

          <div className="space-y-4">

            {/* Customers */}
            <div className="bg-indigo-50 rounded-xl p-4">

              <div className="flex justify-between mb-2">
                <h4 className="font-semibold text-indigo-800">
                  👥 Customers
                </h4>

                <button
                  onClick={() => handleReset(detailRow._id, 'customers')}
                  className="text-xs text-red-500"
                >
                  Reset
                </button>

              </div>

              <UsageBar
                used={detailRow.customersUsed}
                limit={detailRow.customersLimit}
                color="indigo"
              />

            </div>


            {/* Receipt */}
            <div className="bg-blue-50 rounded-xl p-4">

              <div className="flex justify-between mb-2">
                <h4 className="font-semibold text-blue-800">
                  🧾 Receipt Scans
                </h4>

                <button
                  onClick={() => handleReset(detailRow._id, 'receipt')}
                  className="text-xs text-red-500"
                >
                  Reset
                </button>

              </div>

              <UsageBar
                used={detailRow.receiptScanUsed}
                limit={detailRow.receiptScanLimit}
                color="blue"
              />

            </div>


            {/* Manual Invoice */}
            <div className="bg-green-50 rounded-xl p-4">

              <div className="flex justify-between mb-2">
                <h4 className="font-semibold text-green-800">
                  📄 Manual Invoices
                </h4>
              </div>

              <UsageBar
                used={detailRow.manualInvoiceUsed}
                limit={detailRow.manualInvoiceLimit}
                color="green"
              />

            </div>


            {/* Items */}
            <div className="bg-orange-50 rounded-xl p-4">

              <div className="flex justify-between mb-2">
                <h4 className="font-semibold text-orange-800">
                  📦 Items
                </h4>
              </div>

              <UsageBar
                used={detailRow.itemsUsed}
                limit={detailRow.itemsLimit}
                color="orange"
              />

            </div>

          </div>

        </Modal>

      )}

    </div>
  );

};

export default UsageManagement;